import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError, type RecognizeResponse } from '@/api/client';
import { CameraCapture } from '@/components/CameraCapture';
import { DemoLayout } from '@/components/DemoLayout';

type Phase =
  | { kind: 'capturing' }
  | { kind: 'searching' }
  | { kind: 'result'; result: RecognizeResponse }
  | { kind: 'error'; message: string };

/**
 * Reconhecimento: um quadro da câmera → face-search → nome + documento
 * mascarado. Só registros demo resolvem; o resto é "não encontrado".
 */
export function RecognizePage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>({ kind: 'capturing' });

  const handleCapture = useCallback(async (base64Image: string) => {
    setPhase({ kind: 'searching' });
    try {
      const result = await api.recognize(base64Image);
      setPhase({ kind: 'result', result });
    } catch (err) {
      setPhase({
        kind: 'error',
        message: err instanceof ApiError ? err.message : 'Não foi possível reconhecer agora.',
      });
    }
  }, []);

  const reset = () => setPhase({ kind: 'capturing' });

  return (
    <DemoLayout backTo="/" title="Reconhecer">
      {phase.kind === 'capturing' && (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="h2">Quem é você?</Typography>
            <Typography variant="body2" color="text.secondary">
              Olhe para a câmera e capture. Vamos procurar seu rosto entre os cadastros da demonstração.
            </Typography>
          </Box>
          <CameraCapture onCapture={(img) => void handleCapture(img)} hint="Toque para reconhecer" />
        </>
      )}

      {phase.kind === 'searching' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 10 }}>
          <CircularProgress />
          <Typography color="text.secondary">Procurando…</Typography>
        </Box>
      )}

      {phase.kind === 'result' && phase.result.matched && (
        <>
          <Box className="result-card result-card--match" data-testid="recognize-match">
            <Typography variant="h2" sx={{ mb: 1 }}>
              Reconhecido ✓
            </Typography>
            <span className="result-card__label">Nome</span>
            <span className="result-card__value">{phase.result.person.fullName}</span>
            <span className="result-card__label" style={{ marginTop: 8 }}>
              Documento
            </span>
            <span className="result-card__value">{phase.result.person.documentMasked}</span>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Similaridade {Math.round(phase.result.similarity * 100)}% · registro demo
            </Typography>
          </Box>
          <Button variant="contained" fullWidth onClick={reset}>
            Reconhecer outra pessoa
          </Button>
        </>
      )}

      {phase.kind === 'result' && !phase.result.matched && (
        <>
          <Box className="result-card" data-testid="recognize-miss">
            <Typography variant="h2">
              {phase.result.reason === 'NO_FACE' ? 'Nenhum rosto na foto' : 'Não encontrado'}
            </Typography>
            <Typography color="text.secondary">
              {phase.result.reason === 'NO_FACE'
                ? 'Centralize o rosto na câmera, com boa iluminação, e tente de novo.'
                : 'Esse rosto não está entre os cadastros da demonstração. Se acabou de cadastrar, aguarde alguns segundos pela indexação.'}
            </Typography>
          </Box>
          <Button variant="contained" fullWidth onClick={reset}>
            Tentar de novo
          </Button>
          <Button variant="outlined" fullWidth onClick={() => navigate('/cadastrar')}>
            Fazer cadastro
          </Button>
        </>
      )}

      {phase.kind === 'error' && (
        <>
          <Box className="result-card" role="alert">
            <Typography variant="h2">Algo deu errado</Typography>
            <Typography color="text.secondary">{phase.message}</Typography>
          </Box>
          <Button variant="contained" fullWidth onClick={reset}>
            Tentar de novo
          </Button>
        </>
      )}
    </DemoLayout>
  );
}
